/**
 * Custom slash menu items for BB Docs.
 *
 * Uses BlockNote's native SuggestionMenu system.
 * - Merges default BlockNote items with custom commands from the registry
 * - Applies context-aware ranking based on doc type and active agent
 * - Returns items compatible with SuggestionMenuController's getItems prop
 */

import {
  BlockNoteEditor,
  filterSuggestionItems,
  insertOrUpdateBlock,
} from '@blocknote/core';
import {
  getDefaultReactSlashMenuItems,
  DefaultReactSuggestionItem,
} from '@blocknote/react';
import { createElement } from 'react';

import {
  allCommands,
  docTypeAgentPriority,
  type SlashCommand,
} from './registry';
import { agentIdFromKey, agentDisplayName, openAgentChat } from './agentCommands';
import { rewriteBlock, expandBlock, summariseBlock, translateBlock } from './aiCommands';
import { saveBeat } from './beatCommand';
import { detectDocType, getActiveAgent } from './contextDetection';

// ---- Icon helper -----------------------------------------------------------

/**
 * Create a simple text-based icon element for custom menu items.
 * BlockNote's default items use JSX icons; we use a styled span.
 */
function textIcon(label: string): JSX.Element {
  return createElement(
    'span',
    {
      style: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 24,
        height: 24,
        borderRadius: 4,
        background: '#f0f0f0',
        fontSize: label.length > 2 ? 9 : 12,
        fontWeight: 600,
        color: '#37352f',
        fontFamily: 'monospace',
      },
    },
    label,
  );
}

// ---- Command -> MenuItem mapping -------------------------------------------

/**
 * Convert a registry SlashCommand into a BlockNote DefaultReactSuggestionItem
 * by binding the appropriate handler.
 */
function commandToMenuItem(
  cmd: SlashCommand,
  editor: BlockNoteEditor,
): DefaultReactSuggestionItem {
  return {
    title: cmd.title,
    subtext: cmd.subtext,
    group: cmd.group,
    aliases: cmd.aliases,
    badge: cmd.badge,
    icon: textIcon(cmd.emoji),
    onItemClick: () => handleCommand(cmd.key, editor),
  };
}

/**
 * Route command key to the appropriate handler.
 */
function handleCommand(key: string, editor: BlockNoteEditor): void {
  // Block insertion commands
  switch (key) {
    case 'heading_1':
      insertOrUpdateBlock(editor, { type: 'heading', props: { level: 1 } });
      return;
    case 'heading_2':
      insertOrUpdateBlock(editor, { type: 'heading', props: { level: 2 } });
      return;
    case 'heading_3':
      insertOrUpdateBlock(editor, { type: 'heading', props: { level: 3 } });
      return;
    case 'table':
      insertOrUpdateBlock(editor, { type: 'table' as any });
      return;
    case 'code_block':
      insertOrUpdateBlock(editor, { type: 'codeBlock' as any });
      return;
    case 'blockquote':
      // BlockNote doesn't have a native blockquote type in default schema.
      // Insert a paragraph with a visual indicator for now.
      insertOrUpdateBlock(editor, {
        type: 'paragraph',
        content: [{ type: 'text', text: '> ', styles: { italic: true } }],
      });
      return;
    case 'bullet_list':
      insertOrUpdateBlock(editor, { type: 'bulletListItem' });
      return;
    case 'numbered_list':
      insertOrUpdateBlock(editor, { type: 'numberedListItem' });
      return;
    case 'check_list':
      insertOrUpdateBlock(editor, { type: 'checkListItem' });
      return;

  // Media
  case 'image':
    insertOrUpdateBlock(editor, { type: 'image' });
    return;
  case 'video':
    window.dispatchEvent(new CustomEvent('bb-docs:insert-video', { detail: { aiGenerated: false } }));
    return;
  case 'ai_video':
    window.dispatchEvent(new CustomEvent('bb-docs:insert-video', { detail: { aiGenerated: true } }));
    return;

    // Agent commands
    case 'agent_verhaal':
    case 'agent_sove':
    case 'agent_kokkin':
    case 'agent_wetenschap':
    case 'agent_kaart':
    case 'agent_ziel': {
      const agentId = agentIdFromKey(key);
      openAgentChat(agentId, agentDisplayName(agentId));
      return;
    }

    // AI commands
    case 'ai_rewrite':
      rewriteBlock(editor);
      return;
    case 'ai_expand':
      expandBlock(editor);
      return;
    case 'ai_summarise':
      summariseBlock(editor);
      return;
    case 'ai_translate':
      translateBlock(editor);
      return;

    // StoryKeeper commands
    case 'sk_beat':
      saveBeat(editor);
      return;
    case 'frontmatter':
      insertOrUpdateBlock(editor, {
        type: 'codeBlock' as any,
        props: { language: 'yaml' } as any,
        content: [
          {
            type: 'text',
            text: '---\ntitle: \ntype: \ntags: []\n---',
            styles: {},
          },
        ],
      });
      return;

    default:
      console.warn(`Unknown slash command: ${key}`);
  }
}

// ---- Context-aware ranking -------------------------------------------------

/**
 * Rank items based on doc type and active agent.
 * Priority items get moved to the top of their group.
 */
function rankItems(
  items: DefaultReactSuggestionItem[],
  editor: BlockNoteEditor,
): DefaultReactSuggestionItem[] {
  const docType = detectDocType(editor);
  const activeAgentId = getActiveAgent();
  const priorityAgentKey = docTypeAgentPriority[docType];

  if (!priorityAgentKey && !activeAgentId) {
    return items;
  }

  // Build priority set
  const priorityTitles = new Set<string>();

  if (activeAgentId) {
    // Active agent always appears first
    const activeTitle = agentDisplayName(activeAgentId as any);
    priorityTitles.add(activeTitle);
  }

  if (priorityAgentKey) {
    const agentId = agentIdFromKey(priorityAgentKey);
    priorityTitles.add(agentDisplayName(agentId));
  }

  // Stable sort: priority items first, rest in original order
  return [...items].sort((a, b) => {
    const aPriority = priorityTitles.has(a.title) ? 0 : 1;
    const bPriority = priorityTitles.has(b.title) ? 0 : 1;
    return aPriority - bPriority;
  });
}

// ---- Public API ------------------------------------------------------------

/**
 * Get all slash menu items (default BlockNote + custom BB Docs commands).
 * This function is passed to SuggestionMenuController's getItems prop.
 */
export function getCustomSlashMenuItems(
  editor: BlockNoteEditor,
): (query: string) => Promise<DefaultReactSuggestionItem[]> {
  return async (query: string) => {
    // Get BlockNote's default items (heading, paragraph, etc.)
    const defaultItems = getDefaultReactSlashMenuItems(editor);

    // Convert registry commands to menu items
    const customItems = allCommands.map((cmd) => commandToMenuItem(cmd, editor));

    // Merge: custom items come after defaults to avoid replacing built-in ones.
    // We deduplicate by checking if a default item already covers a command
    // (e.g., BlockNote already has Heading 1, Heading 2, etc.)
    const defaultTitles = new Set(defaultItems.map((item) => item.title));
    const uniqueCustomItems = customItems.filter(
      (item) => !defaultTitles.has(item.title),
    );

    const allItems = [...defaultItems, ...uniqueCustomItems];

    // Apply context-aware ranking
    const ranked = rankItems(allItems, editor);

    // Filter by query using BlockNote's built-in filter
    return filterSuggestionItems(ranked, query);
  };
}
